import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CheckIcon, UploadIcon } from 'assets/icons';
import { useAuthProtectedAction } from 'services/hooks/user';
import { selectHasUploadedAudio } from 'services/store/ai/selectors';
import { actions } from 'services/store/ai';
import { DEFAULT_FORMULA_VALUES } from 'services/store/ai/constants';
import { FormulaFields } from 'services/store/ai/types';
import useAnalytics from 'services/hooks/analytics';
import { events } from 'services/analytics';
import useUrlLocation from 'services/hooks/urlLocation';
import { type UploadLoopData } from '../UserLoopModal';
import { UserLoopModalContainer } from './components/UserLoopModalContainer';
import {
  StyledChip,
  StyledContainer,
  StyledGlassBox,
  StyledInfoTypography,
  StyledStateBox,
  StyledStateTypography,
  StyledWrapper,
} from './styled';

export const UserLoop = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const location = useUrlLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen]);
  const protectedOpenModal = useAuthProtectedAction(openModal);

  const hasUploadedAudio = useSelector(selectHasUploadedAudio);

  const handleLoopUpload = useCallback(
    (data: UploadLoopData) => {
      const { id, file, instrument } = data;
      dispatch(
        actions.addFormulaField({
          field: FormulaFields.UserLoop,
          value: {
            ...DEFAULT_FORMULA_VALUES[FormulaFields.UserLoop],
            loopId: id,
            loopFileName: file.name,
            instrument,
          },
        })
      );
    },
    [dispatch]
  );

  const handleClick = useCallback(() => {
    if (hasUploadedAudio) {
      return;
    }

    track(events.audioUploadClick(location));
    protectedOpenModal();
  }, [hasUploadedAudio, location, protectedOpenModal, track]);

  return (
    <>
      <StyledContainer
        hasUploadedAudio={hasUploadedAudio}
        onClick={handleClick}
      >
        <StyledGlassBox>
          <StyledWrapper>
            <StyledChip variant="filled" label={t('general:beta')} />
            <StyledInfoTypography>
              {t('aiMusicGenerator:userLoop:info')}
            </StyledInfoTypography>
          </StyledWrapper>
          <StyledStateBox>
            {hasUploadedAudio ? (
              <>
                <CheckIcon />
                <StyledStateTypography>
                  {t('aiMusicGenerator:userLoop:audioAdded')}
                </StyledStateTypography>
              </>
            ) : (
              <>
                <UploadIcon />
                <StyledStateTypography>
                  {t('aiMusicGenerator:userLoop:uploadAudio')}
                </StyledStateTypography>
              </>
            )}
          </StyledStateBox>
        </StyledGlassBox>
      </StyledContainer>

      <UserLoopModalContainer
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleLoopUpload}
      />
    </>
  );
};

export default UserLoop;
